import { z } from 'zod'
import {
  CRM_LEAD_CONTRACT_VERSION,
  CRM_LEAD_ERROR_CODES,
  crmLeadErrorEnvelopeSchema,
  crmLeadFrontendFieldsSchema,
  crmLeadSummarySchema,
} from './crm-lead-read'

const optionalUnknown = z.unknown().optional()

/**
 * Transport request contracts intentionally stay loose. The handwritten CRM
 * validators own request-body precedence, coercion, and 400 messages; these
 * schemas preserve the legacy camelCase/snake_case surface for clients and
 * independently-authored contract fixtures.
 */
export const crmLeadCreateRequestSchema = z.object({
  companyName: optionalUnknown,
  company_name: optionalUnknown,
  company: optionalUnknown,
  name: optionalUnknown,
  contactName: optionalUnknown,
  contact_name: optionalUnknown,
  firstName: optionalUnknown,
  first_name: optionalUnknown,
  lastName: optionalUnknown,
  last_name: optionalUnknown,
  email: optionalUnknown,
  contactEmail: optionalUnknown,
  contact_email: optionalUnknown,
  phone: optionalUnknown,
  source: optionalUnknown,
  programInterest: optionalUnknown,
  program_interest: optionalUnknown,
  title: optionalUnknown,
  notes: optionalUnknown,
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  stage: optionalUnknown,
  status: optionalUnknown,
  tags: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmLeadPatchRequestSchema = z.object({
  companyName: optionalUnknown,
  company_name: optionalUnknown,
  company: optionalUnknown,
  name: optionalUnknown,
  contactName: optionalUnknown,
  contact_name: optionalUnknown,
  firstName: optionalUnknown,
  first_name: optionalUnknown,
  lastName: optionalUnknown,
  last_name: optionalUnknown,
  email: optionalUnknown,
  contactEmail: optionalUnknown,
  contact_email: optionalUnknown,
  phone: optionalUnknown,
  source: optionalUnknown,
  programInterest: optionalUnknown,
  program_interest: optionalUnknown,
  title: optionalUnknown,
  notes: optionalUnknown,
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  stage: optionalUnknown,
  status: optionalUnknown,
  stageReason: optionalUnknown,
  stage_reason: optionalUnknown,
  reason: optionalUnknown,
  tags: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmLeadAssignRequestSchema = z.object({
  ownerUserId: optionalUnknown,
  owner_user_id: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmLeadStageRequestSchema = z.object({
  toStage: optionalUnknown,
  to_stage: optionalUnknown,
  reason: optionalUnknown,
  stageReason: optionalUnknown,
  stage_reason: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
}).passthrough()

export const crmLeadConvertRequestSchema = z.object({
  projectName: optionalUnknown,
  project_name: optionalUnknown,
  respondentExternalId: optionalUnknown,
  respondent_external_id: optionalUnknown,
  formKey: optionalUnknown,
  form_key: optionalUnknown,
  notes: optionalUnknown,
  idempotencyKey: optionalUnknown,
  idempotency_key: optionalUnknown,
  amountCents: optionalUnknown,
  amount_minor_units: optionalUnknown,
  currency: optionalUnknown,
  expectedClose: optionalUnknown,
  expected_close: optionalUnknown,
}).passthrough()

export const crmLeadWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

/** Shared response shape for create, patch, assignment, and stage writes. */
export const crmLeadMutationResponseSchema = crmLeadFrontendFieldsSchema.extend({
  contract_version: z.literal(CRM_LEAD_CONTRACT_VERSION),
  lead: crmLeadSummarySchema,
  sideEffects: crmLeadWriteSideEffectsSchema,
}).passthrough()

export const crmLeadCreateResponseSchema = crmLeadMutationResponseSchema.extend({
  replayed: z.boolean(),
})
export const crmLeadPatchResponseSchema = crmLeadMutationResponseSchema
export const crmLeadAssignResponseSchema = crmLeadMutationResponseSchema
export const crmLeadStageResponseSchema = crmLeadMutationResponseSchema

const nullableString = z.string().nullable()

const crmLeadConversionEvidenceSchema = z.object({
  projectName: z.string(),
  respondentExternalId: nullableString,
  formKey: nullableString,
  notesPresent: z.boolean(),
  crmLeadMarkedConverted: z.literal(true),
  auditWritten: z.boolean(),
  stratalystHandoffReady: z.literal(false),
  stratalystTouched: z.literal(false),
  stratalystProjectCreated: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
  companyId: z.string(),
  contactId: z.string(),
  opportunityId: z.string(),
  opportunityStage: z.string(),
  companyCreated: z.boolean(),
  contactCreated: z.boolean(),
  opportunityCreated: z.boolean(),
  replayed: z.boolean(),
}).passthrough()

export const crmLeadConvertResponseSchema = z.object({
  contract_version: z.literal(CRM_LEAD_CONTRACT_VERSION),
  lead: crmLeadSummarySchema,
  sideEffects: crmLeadWriteSideEffectsSchema,
  conversion: crmLeadConversionEvidenceSchema,
}).passthrough()

export const CRM_LEAD_WRITE_ERROR_CODES = CRM_LEAD_ERROR_CODES
export const crmLeadWriteErrorEnvelopeSchema = crmLeadErrorEnvelopeSchema

export type CrmLeadCreateRequest = z.infer<typeof crmLeadCreateRequestSchema>
export type CrmLeadPatchRequest = z.infer<typeof crmLeadPatchRequestSchema>
export type CrmLeadAssignRequest = z.infer<typeof crmLeadAssignRequestSchema>
export type CrmLeadStageRequest = z.infer<typeof crmLeadStageRequestSchema>
export type CrmLeadConvertRequest = z.infer<typeof crmLeadConvertRequestSchema>
export type CrmLeadWriteSideEffects = z.infer<typeof crmLeadWriteSideEffectsSchema>
export type CrmLeadMutationResponse = z.infer<typeof crmLeadMutationResponseSchema>
export type CrmLeadCreateResponse = z.infer<typeof crmLeadCreateResponseSchema>
export type CrmLeadPatchResponse = z.infer<typeof crmLeadPatchResponseSchema>
export type CrmLeadAssignResponse = z.infer<typeof crmLeadAssignResponseSchema>
export type CrmLeadStageResponse = z.infer<typeof crmLeadStageResponseSchema>
export type CrmLeadConvertResponse = z.infer<typeof crmLeadConvertResponseSchema>
export type CrmLeadWriteErrorEnvelope = z.infer<typeof crmLeadWriteErrorEnvelopeSchema>
